import React, { useContext, useEffect, useRef, useState } from 'react';
import { ParkingCampusWithFloors } from "../../../../../../../types/demo";
import Label from "../../../../../../../Components/Label";
import { InputText } from "primereact/inputtext";
import callToast, { handleChangeData, postData, validateFormFields } from "../../../../../../../utils/helper";
import { Dropdown } from "primereact/dropdown";
import { InputTextarea } from "primereact/inputtextarea";
import { Button } from "primereact/button";
import { webInfo } from "../../../../../../../utils/webInfo";
import { Toast } from "primereact/toast";
import { LayoutContext } from "../../../../../../../layout/context/LayoutContext";
import { useNavigate } from "react-router-dom";
import { routes } from "../../../../../../../Router/routes";

// Mapbox Imports
import mapboxgl from 'mapbox-gl';
// @ts-ignore
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';
import 'mapbox-gl/dist/mapbox-gl.css';
import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css';
import PageHeading from "../../../../../../../Components/PageHeading";

const VehicleParkingCampusesAdd = () => {
    const { accessToken } = useContext(LayoutContext);
    const navigate = useNavigate();
    const toast = useRef<Toast | null>(null);

    // Map refs
    const mapContainerRef = useRef<HTMLDivElement>(null);
    const mapRef = useRef<mapboxgl.Map | null>(null);
    const markerRef = useRef<mapboxgl.Marker | null>(null);

    const initialState: ParkingCampusWithFloors = {
        name: '',
        address: '',
        latitude: null,
        longitude: null,
        status: { id: 1, name: 'Active' },
        floors: [
            {
                id: Date.now(),
                floor_name: '',
                floor_level: null,
                floor_description: ''
            }
        ]
    };

    const [formData, setFormData] = useState<ParkingCampusWithFloors>(initialState);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

    const isFloorRowTouched = (floor: any) => {
        return (
            (floor?.floor_name?.trim() ?? '') !== '' ||
            (floor?.floor_level !== null && floor?.floor_level !== '') ||
            (floor?.floor_description?.trim() ?? '') !== ''
        );
    };

    const statuses = [
        { id: 1, name: 'Active' },
        { id: 0, name: 'Inactive' },
    ];

    useEffect(() => {
        mapboxgl.accessToken = webInfo?.mapBoxToken;
        if (!mapContainerRef.current) return;

        mapRef.current = new mapboxgl.Map({
            container: mapContainerRef.current,
            style: 'mapbox://styles/mapbox/streets-v12',
            center: [90.4125, 23.8103],
            zoom: 11
        });

        markerRef.current = new mapboxgl.Marker({ draggable: true, color: "#ef4444" })
            .setLngLat([90.4125, 23.8103])
            .addTo(mapRef.current);

        // Add dragend event to update formData when marker is moved
        markerRef.current.on('dragend', () => {
            const lngLat = markerRef.current!.getLngLat();
            setFormData(prev => ({
                ...prev,
                longitude: parseFloat(lngLat.lng.toFixed(6)),
                latitude: parseFloat(lngLat.lat.toFixed(6))
            }));
        });

        mapRef.current.on('click', (e) => {
            const { lng, lat } = e.lngLat;
            setFormData(prev => ({ ...prev, longitude: parseFloat(lng.toFixed(6)), latitude: parseFloat(lat.toFixed(6)) }));
            markerRef.current?.setLngLat([lng, lat]);
        });

        const geocoder = new MapboxGeocoder({
            accessToken: mapboxgl.accessToken,
            mapboxgl: mapboxgl,
            marker: false,
            countries: 'bd',
            placeholder: 'Search location...'
        });
        mapRef.current.addControl(geocoder, 'top-left');

        geocoder.on('result', (e: any) => {
            const [lng, lat] = e.result.geometry.coordinates;
            setFormData(prev => ({ ...prev, longitude: parseFloat(lng.toFixed(6)), latitude: parseFloat(lat.toFixed(6)) }));
            markerRef.current?.setLngLat([lng, lat]);
        });

        return () => mapRef.current?.remove();
    }, []);

    console.log("after change long lat", formData);

    const addField = () => {
        setFormData({
            ...formData,
            floors: [
                ...formData.floors,
                { id: Date.now(), floor_name: '', floor_level: null, floor_description: '' },
            ],
        });
    };

    const handleRemoveRow = (id: number) => {
        setFormData({
            ...formData,
            floors: formData.floors.filter(floor => floor.id !== id)
        });
    };

    const handleFloorChange = (idx: number, field: string, value: any) => {
        const updatedFloors = [...formData.floors];
        updatedFloors[idx] = { ...updatedFloors[idx], [field]: value };
        setFormData({ ...formData, floors: updatedFloors });
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const validationRules = [
            { field: 'name', label: 'Name' },
            { field: 'address', label: 'Address' }
        ];

        if (!validateFormFields(formData, validationRules, toast)) return;

        for (let i = 0; i < formData.floors.length; i++) {
            const floor = formData.floors[i];
            if (isFloorRowTouched(floor) && !floor.floor_name?.trim()) {
                callToast(toast, false, `Floor Name is required in row ${i + 1}`);
                return;
            }
        }

        const payload = {
            name: formData.name.replace(/\s+/g, ' ').trim(),
            status: formData.status?.id,
            address: formData.address.replace(/\s+/g, ' ').trim(),
            latitude: formData.latitude?.toString(),
            longitude: formData.longitude?.toString(),
            floors: formData.floors
                .filter(floor =>
                    (floor?.floor_name?.trim() ?? '') !== '' ||
                    (floor?.floor_level !== null && floor?.floor_level !== undefined) ||
                    (floor?.floor_description?.trim() ?? '') !== ''
                )
                .map(floor => ({
                    floor_name: floor?.floor_name?.replace(/\s+/g, ' ').trim(),
                    floor_level: floor.floor_level,
                    floor_description: floor?.floor_description?.replace(/\s+/g, ' ').trim()
                }))

        };

        try {
            setIsSubmitting(true);
            const response = await fetch(`${webInfo?.baseUrl}/vehicle-parking-campuses`, postData("PUT", accessToken, payload));
            if (response.ok) {
                callToast(toast, true, "Parking Campus created successfully");
                setTimeout(() => navigate(routes.settingsConfigureVehicleParkingCampuses), 1000);
            } else {
                const resData = await response.json();
                callToast(toast, false, resData.error || "Failed to save");
            }
        } catch (err) {
            console.error(err);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="card">
            <Toast ref={toast} />
            <PageHeading title="Add Parking Campus" back={true} />

            <form onSubmit={handleSubmit}>
                {/* Main Info */}
                <div className="grid">
                    <div className="col-12 md:col-3">
                        <Label label={"Name"} required />
                        <InputText
                            value={formData.name}
                            onChange={(e) => handleChangeData('name', e.target.value, formData, setFormData)}
                            placeholder={"Enter campus name"}
                            className="w-full"
                        />
                    </div>
                    <div className="col-12 md:col-2">
                        <Label label={"Status"} required />
                        <Dropdown
                            options={statuses}
                            optionLabel="name"
                            value={formData.status}
                            onChange={(e) => handleChangeData('status', e.value, formData, setFormData)}
                            className="w-full"
                        />
                    </div>
                </div>

                <div className="grid mt-2">
                    <div className="col-12 md:col-6">
                        <Label label={"Address"} required />
                        <InputTextarea
                            value={formData.address}
                            onChange={(e) => handleChangeData('address', e.target.value, formData, setFormData)}
                            rows={3}
                            className="w-full"
                            placeholder="Enter campus address"
                        />
                    </div>
                </div>

                {/* Floors Section */}
                <div className="gird col-8 mt-2">
                    <Label label={"Floors"} />
                    {formData.floors.map((floor, idx) => {
                        const rowRequired = isFloorRowTouched(floor);

                        return (
                            <div key={floor.id} className="border-2 border-gray-100 border-round-lg p-4 mb-2">
                                <div className="flex justify-content-end mb-1">
                                    {formData.floors.length > 1 && (
                                        <Button
                                            type="button"
                                            icon="pi pi-fw pi-times"
                                            outlined
                                            severity="danger"
                                            onClick={() => handleRemoveRow(floor.id as number)}
                                            className="border-circle w-2rem h-2rem"
                                        />
                                    )}
                                </div>

                                <div className="grid align-items-center gap-3">
                                    <div className="col-12 md:col-6">
                                        <Label label="Floor Name" required={rowRequired} />
                                        <InputText
                                            value={floor.floor_name}
                                            onChange={(e) => handleFloorChange(idx, 'floor_name', e.target.value)}
                                            placeholder="Enter floor name"
                                            className="w-full"
                                        />
                                    </div>
                                    <div className="col-12 md:col-3">
                                        <Label label="Floor Level" required={rowRequired} />
                                        <InputText
                                            type="number"
                                            value={floor.floor_level?.toString() || ''}
                                            onChange={(e) => handleFloorChange(idx, 'floor_level', e.target.value)}
                                            placeholder="Select level"
                                            className="w-full"
                                        />
                                    </div>
                                </div>

                                <div className="grid mt-2">
                                    <div className="col-12">
                                        <Label label="Description" />
                                        <InputTextarea
                                            value={floor.floor_description}
                                            onChange={(e) => handleFloorChange(idx, 'floor_description', e.target.value)}
                                            placeholder="Enter floor description"
                                            className="w-full"
                                            rows={2}
                                        />
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Map Section */}
                <div className="grid mt-2">
                    <div className="col-12 ">
                        <Label label={"Location Pin"} />
                        <div ref={mapContainerRef} style={{ height: '350px', width: '100%', borderRadius: '8px', border: '1px solid #dee2e6' }} />
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2 mt-4">
                    <Button type="submit" label="Create" className="p-button-primary" size={'small'} loading={isSubmitting} />
                    <Button type="button" icon="pi pi-fw pi-plus" label="Add Row" onClick={addField} className="p-button-outlined" size="small" />
                </div>
            </form>
        </div>
    );
};

export default VehicleParkingCampusesAdd;
